import { DatePicker } from "@/components/generic/DatePicker";
import { NumberField } from "@/components/generic/NumberField";
import { Select } from "@/components/generic/Select";
import { FilterIdentifier } from "@/data/filters/config";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { EventQueryFilter } from "@/types/campaign";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateValue, parseDate } from "@internationalized/date";
import { upperFirst } from "lodash-es";
import { useEffect } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";
import { SelectItem } from "../generic/Select";

// Define time units
const timeUnits = [
  { id: "days", label: "dage siden" },
  { id: "months", label: "måneder siden" },
  { id: "years", label: "år siden" },
];

const schema = z.object({
  // Relative mode fields
  greaterThanValue: z.number().nullable(),
  lessThanValue: z.number().nullable(),
  timeUnit: z.enum(["days", "months", "years"]).default("days"),

  // Absolute date mode fields
  afterDate: z.custom<DateValue>().nullable(),
  beforeDate: z.custom<DateValue>().nullable(),
});
// TODO - fix refinement. Currently causes submit to get empty object instead of the form data
// .superRefine((data, ctx) => {
//   if (data.greaterThanValue || data.lessThanValue) {
//     if (
//       (data.greaterThanValue !== null && data.greaterThanValue < 0) ||
//       (data.lessThanValue !== null && data.lessThanValue < 0)
//     ) {
//       ctx.addIssue({
//         path: [
//           data.greaterThanValue !== null
//             ? "greaterThanValue"
//             : "lessThanValue",
//         ],
//         code: z.ZodIssueCode.custom,
//         message: "Værdierne skal være positive",
//       });
//     }

//     if (
//       data.greaterThanValue !== null &&
//       data.lessThanValue !== null &&
//       data.greaterThanValue < data.lessThanValue
//     ) {
//       ctx.addIssue({
//         path: ["greaterThanValue"],
//         code: z.ZodIssueCode.custom,
//         message: 'Værdierne skal være "mere end" mindre end',
//       });
//     }

//     if (data.afterDate !== null || data.beforeDate !== null) {
//       ctx.addIssue({
//         path: ["root"],
//         code: z.ZodIssueCode.custom,
//         message:
//           "Du kan ikke bruge både datoer og relative værdier samtidigt",
//       });
//     }
//   } else if (!data.greaterThanValue && !data.lessThanValue) {
//     if (data.afterDate == null && data.beforeDate == null) {
//       ctx.addIssue({
//         path: ["greaterThanValue", "lessThanValue"],
//         code: z.ZodIssueCode.custom,
//         message: "Mindst ét felt skal udfyldes",
//       });
//     }
//   }
// });

export function EventFilter({
  group: { ...props },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<"value", { eventType: string }>) {
  let addressChangeFilter = queryFilters.value;
  if (addressChangeFilter?.operator !== "EventFilter")
    addressChangeFilter = undefined;

  // Parse existing filter to form values
  const getDefaultValues = () => {
    const defaultValues = {
      greaterThanValue: null as number | null,
      lessThanValue: null as number | null,
      timeUnit: "days" as "days" | "months" | "years",
      afterDate: null as DateValue | null,
      beforeDate: null as DateValue | null,
    };

    if (!addressChangeFilter) return defaultValues;

    switch (addressChangeFilter.timeOperator) {
      case "MoreThanDaysAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(addressChangeFilter.timeValues?.[0]),
          timeUnit: "days" as const,
        };
      case "MoreThanMonthsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(addressChangeFilter.timeValues?.[0]),
          timeUnit: "months" as const,
        };
      case "MoreThanYearsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(addressChangeFilter.timeValues?.[0]),
          timeUnit: "years" as const,
        };
      case "LessThanDaysAgo":
        return {
          ...defaultValues,
          lessThanValue: Number(addressChangeFilter.timeValues?.[0]),
          timeUnit: "days" as const,
        };
      case "LessThanMonthsAgo":
        return {
          ...defaultValues,
          lessThanValue: Number(addressChangeFilter.timeValues?.[0]),
          timeUnit: "months" as const,
        };
      case "LessThanYearsAgo":
        return {
          ...defaultValues,
          lessThanValue: Number(addressChangeFilter.timeValues?.[0]),
          timeUnit: "years" as const,
        };
      case "Between":
        return {
          ...defaultValues,
          afterDate: addressChangeFilter.timeValues
            ? parseDate(addressChangeFilter.timeValues[0])
            : null,
          beforeDate: addressChangeFilter.timeValues
            ? parseDate(addressChangeFilter.timeValues[1])
            : null,
        };
      case "BetweenDaysAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(addressChangeFilter.timeValues?.[0]),
          lessThanValue: Number(addressChangeFilter.timeValues?.[1]),
          timeUnit: "days" as const,
        };
      case "BetweenMonthsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(addressChangeFilter.timeValues?.[0]),
          lessThanValue: Number(addressChangeFilter.timeValues?.[1]),
          timeUnit: "months" as const,
        };
      case "BetweenYearsAgo":
        return {
          ...defaultValues,
          greaterThanValue: Number(addressChangeFilter.timeValues?.[0]),
          lessThanValue: Number(addressChangeFilter.timeValues?.[1]),
          timeUnit: "years" as const,
        };
      case "After":
        return {
          ...defaultValues,
          afterDate: addressChangeFilter.timeValues
            ? parseDate(addressChangeFilter.timeValues[0])
            : null,
        };
      case "Before":
        return {
          ...defaultValues,
          beforeDate: addressChangeFilter.timeValues
            ? parseDate(addressChangeFilter.timeValues[0])
            : null,
        };
      default:
        return defaultValues;
    }
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: getDefaultValues(),
  });

  const greaterThanValue = watch("greaterThanValue");
  const lessThanValue = watch("lessThanValue");
  const afterDate = watch("afterDate");
  const beforeDate = watch("beforeDate");

  // When relative fields get a value, clear absolute fields
  useEffect(() => {
    if (greaterThanValue !== null || lessThanValue !== null) {
      setValue("afterDate", null);
      setValue("beforeDate", null);
    }
  }, [greaterThanValue, lessThanValue, setValue]);

  // When absolute fields get a value, clear relative fields
  useEffect(() => {
    if (afterDate !== null || beforeDate !== null) {
      setValue("greaterThanValue", null);
      setValue("lessThanValue", null);
    }
  }, [afterDate, beforeDate, setValue]);

  useReportDirty(isDirty, setIsDirty);

  const submit = handleSubmit(
    (data) => {
      const filter: Omit<EventQueryFilter, keyof FilterIdentifier> = {
        operator: "EventFilter",
        eventType: props.eventType,
      };

      // Check if we're using the relative mode
      if (data.greaterThanValue !== null || data.lessThanValue !== null) {
        if (data.greaterThanValue !== null && data.lessThanValue !== null) {
          filter.timeOperator = `Between${upperFirst(
            data.timeUnit,
          )}Ago` as const;
          filter.timeValues = [
            data.greaterThanValue.toString(),
            data.lessThanValue.toString(),
          ];
        } else if (data.greaterThanValue !== null) {
          filter.timeOperator = `MoreThan${upperFirst(
            data.timeUnit,
          )}Ago` as const;
          filter.timeValues = [data.greaterThanValue.toString()];
        } else if (data.lessThanValue !== null) {
          filter.timeOperator = `LessThan${upperFirst(
            data.timeUnit,
          )}Ago` as const;
          filter.timeValues = [data.lessThanValue.toString()];
        }
      }
      // Otherwise use date mode
      else {
        if (data.afterDate && data.beforeDate) {
          // If both dates are provided, use Between
          filter.timeOperator = "Between" as const;
          filter.timeValues = [
            data.afterDate.toString(),
            data.beforeDate.toString(),
          ];
        } else if (data.afterDate) {
          // Only after date
          filter.timeOperator = "After" as const;
          filter.timeValues = [data.afterDate.toString()];
        } else if (data.beforeDate) {
          // Only before date
          filter.timeOperator = "Before" as const;
          filter.timeValues = [data.beforeDate.toString()];
        }
      }

      update({
        value: {
          ...filter,
          operator: "EventFilter",
          eventType: props.eventType,
        },
      });
    },
    (errors) => {
      console.error("Form submission error", errors);
    },
  );

  return (
    <div className="flex flex-col gap-y-9">
      <SegmentHeader title={props.title} description={props.description} />
      <Form
        id={formId}
        onSubmit={submit}
        className="flex h-full flex-col items-center gap-y-12"
      >
        <div className="flex w-full gap-4">
          <NumberField
            control={control}
            name="greaterThanValue"
            placeholder="Mere end"
            aria-label="Mere end"
            className="flex-1"
          />
          <NumberField
            control={control}
            name="lessThanValue"
            placeholder="Mindre end"
            aria-label="Mindre end"
            className="flex-1"
          />
          <Controller
            control={control}
            name="timeUnit"
            render={({ field, fieldState: { error } }) => (
              <Select
                placeholder="Enhed"
                aria-label="Enhed"
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                items={timeUnits}
                errorMessage={error?.message}
              >
                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
              </Select>
            )}
          />
        </div>
        <div className="text-sm font-medium text-neutral-400">eller</div>
        <div className="flex w-full gap-4 *:w-1/2">
          <Controller
            control={control}
            name="afterDate"
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                aria-label="Adresseændring efter"
                label="Efter den"
              />
            )}
          />
          <Controller
            control={control}
            name="beforeDate"
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                aria-label="Adresseændring før"
                label="Før den"
              />
            )}
          />
        </div>
      </Form>
    </div>
  );
}
